import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { publicApi, orderApi } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ArrowLeft, Copy, CheckCircle2, Truck, RefreshCw } from "lucide-react";
import PayPalHostedButton from "@/components/PayPalHostedButton";

const COUNTRIES = [
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "IE", name: "Ireland" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "SG", name: "Singapore" },
  { code: "JP", name: "Japan" },
  { code: "OTHER", name: "Other" },
];

export default function Checkout() {
  const {
    items,
    subtotal,
    shippingCost,
    totalPrice,
    shippingOption,
    setShippingOption,
    hasPhysicalItems,
    clear,
  } = useCart();

  const [config, setConfig] = useState({
    payid: { alias: "", account_name: "Cameron Boyle" },
    paypal: { client_id: "", hosted_button_id: "" },
  });
  const [paymentMethod, setPaymentMethod] = useState("payid");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postcode: "",
    country_code: "AU",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState(null); // {options, source, message}
  const navigate = useNavigate();

  useEffect(() => {
    publicApi.getConfig().then(setConfig).catch(() => {});
  }, []);

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    // Invalidate quote when address changes
    if (["postcode", "country_code"].includes(k)) {
      setShippingOption(null);
      setQuote(null);
    }
  };

  const requestQuote = async () => {
    if (!hasPhysicalItems) {
      toast.success("Digital delivery — no shipping needed");
      setShippingOption({ code: "FREE", name: "Digital delivery", price: 0 });
      return;
    }
    if (!form.postcode) {
      toast.error("Enter your postcode first");
      return;
    }
    if (form.country_code === "AU" && !/^\d{4}$/.test(form.postcode.trim())) {
      toast.error("Australian postcode must be 4 digits");
      return;
    }
    setQuoting(true);
    setShippingOption(null);
    try {
      const payload = {
        postcode: form.postcode.trim(),
        country_code: form.country_code === "OTHER" ? "US" : form.country_code,
        items: items.map((i) => ({
          product_id: i.id,
          title: i.title,
          qty: i.qty,
          category: i.category || "other",
        })),
      };
      const data = await publicApi.quoteShipping(payload);
      setQuote(data);
      if (data.options?.length) {
        setShippingOption(data.options[0]);
      }
      if (data.message) {
        toast.info(data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't fetch shipping quote");
    } finally {
      setQuoting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "address_line1", "city", "state", "postcode"];
    for (const k of required) {
      if (!form[k]) {
        toast.error(`Please fill in ${k.replace("_", " ")}`);
        return;
      }
    }
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (hasPhysicalItems && !shippingOption) {
      toast.error("Get a shipping quote and select a delivery option first");
      return;
    }
    setSubmitting(true);
    try {
      const countryName =
        COUNTRIES.find((c) => c.code === form.country_code)?.name || form.country_code;
      const payload = {
        items: items.map((i) => ({
          product_id: i.id,
          title: i.title,
          price: i.price,
          qty: i.qty,
          image_url: i.image_url || null,
        })),
        shipping: {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          address_line1: form.address_line1,
          address_line2: form.address_line2 || null,
          city: form.city,
          state: form.state,
          postcode: form.postcode,
          country: countryName,
          notes: form.notes || null,
        },
        payment_method: paymentMethod,
        shipping_option: shippingOption
          ? {
              code: shippingOption.code,
              name: shippingOption.name,
              price: Number(shippingOption.price) || 0,
              currency: shippingOption.currency || "AUD",
              delivery_time: shippingOption.delivery_time || null,
            }
          : null,
      };
      const created = await orderApi.createOrder(payload);
      setOrder(created);
      clear();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order could not be placed");
    } finally {
      setSubmitting(false);
    }
  };

  if (order) {
    return <OrderConfirmation order={order} config={config} />;
  }

  if (items.length === 0) {
    return (
      <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-24 text-center">
        <h1 className="font-display text-5xl md:text-6xl mb-6">Nothing to check out</h1>
        <p className="text-white/60 mb-8">Add something to your cart first.</p>
        <Link to="/shop" className="btn-primary">Browse the shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-checkout" className="max-w-[1200px] mx-auto px-5 md:px-10 py-12">
      <Link to="/cart" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm uppercase tracking-widest mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to cart
      </Link>
      <h1 className="font-display text-5xl md:text-6xl mb-2">Checkout</h1>
      <p className="text-white/60 mb-10">Pay by PayID (bank transfer, no fees) or PayPal. Shipping calculated live from Mulgrave, VIC via Australia Post.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        <form onSubmit={submit} className="md:col-span-2 space-y-8">
          <section>
            <p className="overline mb-4">Payment method</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className={`cursor-pointer border p-4 flex items-start gap-3 transition-colors ${paymentMethod === "payid" ? "border-[#FF4500] bg-[#FF4500]/[0.05]" : "border-white/20 hover:border-white"}`}>
                <input data-testid="co-pm-payid" type="radio" name="pm" checked={paymentMethod === "payid"} onChange={() => setPaymentMethod("payid")} className="mt-1 accent-[#FF4500]" />
                <div>
                  <p className="font-display tracking-wider">PayID</p>
                  <p className="text-xs text-white/60 mt-1">Australian bank transfer · no fees</p>
                </div>
              </label>
              <label className={`cursor-pointer border p-4 flex items-start gap-3 transition-colors ${paymentMethod === "paypal" ? "border-[#FF4500] bg-[#FF4500]/[0.05]" : "border-white/20 hover:border-white"}`}>
                <input data-testid="co-pm-paypal" type="radio" name="pm" checked={paymentMethod === "paypal"} onChange={() => setPaymentMethod("paypal")} className="mt-1 accent-[#FF4500]" />
                <div>
                  <p className="font-display tracking-wider">PayPal</p>
                  <p className="text-xs text-white/60 mt-1">International · cards & PayPal accounts</p>
                </div>
              </label>
            </div>
          </section>

          <section>
            <p className="overline mb-4">Your details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Full name*</label>
                <input data-testid="co-name" required className="cka-input" value={form.name} onChange={update("name")} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Email*</label>
                <input data-testid="co-email" required type="email" className="cka-input" value={form.email} onChange={update("email")} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Phone</label>
                <input data-testid="co-phone" className="cka-input" value={form.phone} onChange={update("phone")} />
              </div>
            </div>
          </section>

          <section>
            <p className="overline mb-4">Shipping address</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Country*</label>
                <select
                  data-testid="co-country"
                  required
                  className="cka-input"
                  value={form.country_code}
                  onChange={update("country_code")}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code} className="bg-[#141414]">
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Address line 1*</label>
                <input data-testid="co-addr1" required className="cka-input" value={form.address_line1} onChange={update("address_line1")} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Address line 2</label>
                <input data-testid="co-addr2" className="cka-input" value={form.address_line2} onChange={update("address_line2")} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Suburb / City*</label>
                <input data-testid="co-city" required className="cka-input" value={form.city} onChange={update("city")} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">State / Region*</label>
                <input data-testid="co-state" required className="cka-input" value={form.state} onChange={update("state")} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Postcode / ZIP*</label>
                <input data-testid="co-postcode" required className="cka-input" value={form.postcode} onChange={update("postcode")} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Order notes</label>
                <textarea data-testid="co-notes" rows={3} className="cka-input" value={form.notes} onChange={update("notes")} placeholder="Anything Cam needs to know?" />
              </div>
            </div>
          </section>

          {hasPhysicalItems && (
            <section data-testid="co-shipping-section" className="border border-white/10 bg-[#141414] p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#FF4500]" />
                  <p className="overline mb-0">Postage</p>
                </div>
                <button
                  type="button"
                  data-testid="co-get-quote"
                  onClick={requestQuote}
                  disabled={quoting || !form.postcode}
                  className="text-xs uppercase tracking-widest border border-white/20 px-4 py-2 hover:bg-white hover:text-black disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {quoting ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Quoting…
                    </>
                  ) : (
                    <>{quote ? "Refresh quote" : "Get shipping quote"}</>
                  )}
                </button>
              </div>

              {!quote && (
                <p className="text-sm text-white/60">
                  Enter your postcode/country above then tap <strong>Get shipping quote</strong>. Live rates from Australia Post, shipped from Mulgrave, Victoria.
                </p>
              )}

              {quote && quote.options?.length > 0 && (
                <div className="space-y-2" data-testid="co-shipping-options">
                  {quote.options.map((opt) => {
                    const selected = shippingOption?.code === opt.code;
                    return (
                      <label
                        key={opt.code}
                        data-testid={`co-ship-opt-${opt.code}`}
                        className={`cursor-pointer border p-4 flex items-center gap-3 transition-colors ${
                          selected ? "border-[#FF4500] bg-[#FF4500]/[0.05]" : "border-white/15 hover:border-white/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="ship-opt"
                          checked={selected}
                          onChange={() => setShippingOption(opt)}
                          className="accent-[#FF4500]"
                        />
                        <div className="flex-1">
                          <p className="font-display tracking-wider text-sm">{opt.name}</p>
                          {opt.delivery_time && (
                            <p className="text-xs text-white/50 mt-0.5">{opt.delivery_time}</p>
                          )}
                        </div>
                        <p className="text-[#FF4500] font-semibold">
                          ${Number(opt.price).toFixed(2)}
                        </p>
                      </label>
                    );
                  })}
                  {quote.source === "fallback" && (
                    <p className="text-xs text-white/50 mt-2">Estimated rates — final may vary slightly.</p>
                  )}
                </div>
              )}
            </section>
          )}

          <button data-testid="co-submit" type="submit" disabled={submitting || (hasPhysicalItems && !shippingOption)} className="btn-primary text-lg py-4 px-8 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? "Placing order..." : `Place order · $${totalPrice.toFixed(2)}`}
          </button>
        </form>

        <aside className="border border-white/10 bg-[#141414] p-6 h-fit">
          <p className="overline mb-4">Order summary</p>
          <div className="space-y-3 mb-4">
            {items.map((it) => (
              <div key={it.id} className="flex gap-3 text-sm">
                {it.image_url && <img src={it.image_url} alt="" className="w-14 h-14 object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="truncate">{it.title}</p>
                  <p className="text-white/50 text-xs">Qty {it.qty} · ${it.price.toFixed(2)}</p>
                </div>
                <p className="text-[#FF4500] font-semibold">${(it.price * it.qty).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-white/70">
              <span>Subtotal</span>
              <span data-testid="co-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-white/70">
              <span>Shipping</span>
              <span data-testid="co-shipping-cost">
                {shippingOption ? `$${shippingCost.toFixed(2)}` : "—"}
              </span>
            </div>
          </div>
          <div className="border-t border-white/10 pt-4 mt-3 flex justify-between font-display text-xl">
            <span>Total</span>
            <span data-testid="co-total" className="text-[#FF4500]">${totalPrice.toFixed(2)}</span>
          </div>
        </aside>
      </div>
    </div>
  );
}

function OrderConfirmation({ order, config }) {
  const copy = (val) => { navigator.clipboard.writeText(val); toast.success("Copied to clipboard"); };
  const payid = config?.payid || {};
  const paypal = config?.paypal || {};
  const alias = payid.alias || "(PayID coming soon — Cam will email details)";
  const accountName = payid.account_name || "Cameron Boyle";
  const isPayPal = order.payment_method === "paypal";
  return (
    <div data-testid="page-order-confirmation" className="max-w-[900px] mx-auto px-5 md:px-10 py-16">
      <div className="flex items-center gap-3 mb-6">
        <CheckCircle2 className="w-10 h-10 text-[#FF4500]" />
        <div>
          <p className="overline">Order placed</p>
          <h1 className="font-display text-4xl md:text-5xl">Thank you!</h1>
        </div>
      </div>
      <p className="text-white/70 leading-relaxed mb-8">
        Your order has been received. Complete payment via <strong>{isPayPal ? "PayPal" : "PayID"}</strong> below and Cam will confirm shipping once the transfer clears (usually within 24 hours).
      </p>

      {isPayPal ? (
        <div className="border border-[#FF4500]/40 bg-[#FF4500]/[0.05] p-6 mb-6">
          <p className="overline mb-2">Pay with PayPal</p>
          <p className="text-xs text-white/60 mb-4">Complete your ${order.total.toFixed(2)} AUD payment through the secure PayPal button below — or scan the QR with your phone camera to pay via PayPal app.</p>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-start">
            <PayPalHostedButton
              clientId={paypal.client_id}
              buttonId={paypal.hosted_button_id}
              orderReference={order.reference}
            />
            {paypal.qr_url && (
              <div className="bg-white p-3 text-center max-w-[220px] mx-auto md:mx-0">
                <img data-testid="oc-paypal-qr" src={paypal.qr_url} alt="PayPal QR — Cam's Koori Art craft" className="w-40 h-40 object-contain mx-auto" />
                <p className="text-black text-[10px] uppercase tracking-widest mt-2">Scan to pay</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-[#FF4500]/40 bg-[#FF4500]/[0.05] p-6 mb-6">
          <p className="overline mb-4">PayID payment instructions</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50">PayID alias</p>
              <div className="flex items-center gap-2 mt-1">
                <p data-testid="oc-payid-alias" className="font-display text-xl break-all">{alias}</p>
                {payid.alias && <button onClick={() => copy(payid.alias)} className="text-white/60 hover:text-[#FF4500]"><Copy className="w-4 h-4" /></button>}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50">Account name</p>
              <p className="font-display text-xl mt-1">{accountName}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50">Amount</p>
              <div className="flex items-center gap-2 mt-1">
                <p data-testid="oc-amount" className="font-display text-xl text-[#FF4500]">${order.total.toFixed(2)} AUD</p>
                <button onClick={() => copy(order.total.toFixed(2))} className="text-white/60 hover:text-[#FF4500]"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50">Reference (important)</p>
              <div className="flex items-center gap-2 mt-1">
                <p data-testid="oc-reference" className="font-display text-xl break-all">{order.reference}</p>
                <button onClick={() => copy(order.reference)} className="text-white/60 hover:text-[#FF4500]"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
          <p className="text-xs text-white/50 mt-5 border-t border-white/10 pt-4">Include the reference <strong className="text-white">{order.reference}</strong> when making the transfer so Cam can match it to your order.</p>
        </div>
      )}

      <div className="border border-white/10 bg-[#141414] p-6">
        <p className="overline mb-3">Order summary</p>
        <div className="space-y-2 mb-4">
          {order.items.map((it, i) => (
            <div key={`${order.reference}-${it.product_id || "item"}-${i}`} className="flex justify-between text-sm">
              <span>{it.title} <span className="text-white/40">× {it.qty}</span></span>
              <span>${(it.price * it.qty).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-white/70">
            <span>Subtotal</span>
            <span>${(order.subtotal ?? order.total).toFixed(2)}</span>
          </div>
          {order.shipping_cost > 0 && (
            <div className="flex justify-between text-white/70">
              <span>Shipping {order.shipping_option?.name ? `· ${order.shipping_option.name}` : ""}</span>
              <span>${order.shipping_cost.toFixed(2)}</span>
            </div>
          )}
        </div>
        <div className="border-t border-white/10 pt-3 mt-3 flex justify-between font-display text-xl">
          <span>Total</span>
          <span className="text-[#FF4500]">${order.total.toFixed(2)}</span>
        </div>
        <p className="text-xs text-white/50 mt-4">Order reference: <span className="font-mono text-white">{order.reference}</span></p>
      </div>
      <p className="text-white/60 mt-8 text-sm">A copy of your order details is on this page — screenshot or bookmark it. Any questions? <Link to="/contact" className="text-[#FF4500] hover:underline">Get in touch</Link>.</p>
    </div>
  );
}
